import { useEffect, useMemo, useRef, useState } from 'react';

import { getEventDeviceGps } from '../services/fleet';
import { simplifyTrackRDP } from '../plugins/synced_viewer/util';
import { GpsSummary } from '../types';

const POLL_MS = 30_000;
const MAX_TRACK_POINTS = 4096;

interface CacheEntry {
  key: string;
  track: GpsSummary['track'];
}

export interface EventDeviceGpsResult {
  track: GpsSummary['track'];
  loading: boolean;
}

function simplify(track: GpsSummary['track']): GpsSummary['track'] {
  return track.length <= MAX_TRACK_POINTS
    ? track
    : simplifyTrackRDP(track, 0.1, MAX_TRACK_POINTS);
}

/** Merge the polled event path with the fresher session/SSE trail. */
export function mergeGpsTracks(
  eventTrack: GpsSummary['track'],
  sessionTrack: GpsSummary['track'],
): GpsSummary['track'] {
  if (eventTrack.length === 0) return sessionTrack;
  if (sessionTrack.length === 0) return eventTrack;
  const merged = eventTrack.concat(sessionTrack).sort((a, b) => a[2] - b[2]);
  const unique: GpsSummary['track'] = [];
  let lastTs = -Infinity;
  for (const point of merged) {
    if (point[2] <= lastTs) continue;
    unique.push(point);
    lastTs = point[2];
  }
  return simplify(unique);
}

/** Event-wide competitor path, including GPS received between recordings. */
export function useEventDeviceGps(
  eventId: number,
  deviceId: string | null,
  enabled: boolean,
): EventDeviceGpsResult {
  const key = enabled && deviceId ? `${eventId}:${deviceId}` : '';
  const cacheRef = useRef<Map<string, GpsSummary['track']>>(new Map());
  const [entry, setEntry] = useState<CacheEntry | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!key || !deviceId) {
      setEntry(null);
      setLoading(false);
      return;
    }
    const cached = cacheRef.current.get(key);
    setEntry(cached ? { key, track: cached } : null);
    let cancelled = false;

    const refresh = async () => {
      if (!cached) setLoading(true);
      try {
        const response = await getEventDeviceGps(eventId, deviceId);
        if (cancelled) return;
        const track = simplify(response.track);
        cacheRef.current.set(key, track);
        setEntry({ key, track });
      } catch (error) {
        // Rolling deploys may briefly have the new viewer before the backend
        // route. The caller retains its session-only fallback in that case.
        console.warn('[useEventDeviceGps] event path fetch failed', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [deviceId, eventId, key]);

  return useMemo(
    () => ({
      track: entry?.key === key ? entry.track : [],
      loading,
    }),
    [entry, key, loading],
  );
}

export default useEventDeviceGps;
